import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../types/report.types';
import { useSectionPages } from '../SectionPageContext';

interface KeyFindingsPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const DEFAULT_COMPANY = "Capture The Bug";

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 10,
    color: 'grey',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

const KeyFindingsPage: React.FC<KeyFindingsPageProps> = ({ reportData, sectionId }) => {
  const { updateSectionPage } = useSectionPages();

  // We now always show all columns for consistency
  const hasAcceptsRisk = true;
  const hasNonActionable = true;

  // Calculate dynamic column widths (fixed for 6 columns: Severity, Open, Closed, BAR, BNA, Total)
  const columnWidths = { 
    severity: '18%', 
    open: '12%', 
    closed: '12%', 
    acceptsRisk: '22%', 
    nonActionable: '22%', 
    total: '14%' 
  };

  // Generate dynamic severity text based on actual findings
  const generateSeverityText = () => {
    const severities = [];
    const counts = reportData.open_close_counts_by_severity || {};

    if (counts.Critical?.Total > 0) severities.push('critical');
    if (counts.High?.Total > 0) severities.push('high');
    if (counts.Medium?.Total > 0) severities.push('medium');
    if (counts.Low?.Total > 0) severities.push('low');
    if (counts.Info?.Total > 0) severities.push('info');

    if (severities.length === 0) return 'vulnerabilities';
    if (severities.length === 1) return `${severities[0]} vulnerabilities`;
    if (severities.length === 2) return `${severities[0]} and ${severities[1]} vulnerabilities`;

    // For 3 or more severities, use comma separation with "and" before the last
    const lastSeverity = severities.pop();
    return `${severities.join(', ')}, and ${lastSeverity} vulnerabilities`;
  };

  return (
    <Page size="A4" id={sectionId} style={{
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      padding: '20mm 15mm',
      fontFamily: 'Helvetica',
      fontSize: 12,
    }}>
      <View style={{ paddingHorizontal: 14, paddingTop: 10, flexDirection: 'column', flex: 1 }}>
        <View style={{ flex: 1 }}>
          {/* No title here, just paragraphs */}
          {/* Always regenerate if asset_type_counts is provided (fresh backend calculation), otherwise use saved key_findings */}
          {reportData.key_findings && !reportData.asset_type_counts ? (
            <Text style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 18, color: '#374151', textAlign: 'justify' }}>{reportData.key_findings}</Text>
          ) : (
            <>
              {/* Enhanced Severity Paragraph with Asset Type Distribution per Severity */}
              {(() => {
                // Get all unique target types from target_details
                const scopedTypes = Array.from(new Set(reportData.target_details?.map(t => t.type) || []));
                const hasMultipleAssetTypes = scopedTypes.length > 1;

                // Build a map of scope (URL) to target type from target_details
                const scopeToTypeMap = new Map<string, string>();
                (reportData.target_details || []).forEach(target => {
                  if (target.url && target.type) {
                    scopeToTypeMap.set(target.url.toLowerCase().trim(), target.type);
                  }
                });

                // Helper function to extract domain from URL for flexible matching
                const extractDomain = (url: string): string => {
                  let domain = url.replace(/^https?:\/\//, '');
                  domain = domain.split('/')[0];
                  const parts = domain.split('.');
                  if (parts.length >= 2) {
                    return parts.slice(-2).join('.');
                  }
                  return domain;
                };

                // Helper function to find target type for a scope
                const findTargetType = (scope: string): string | undefined => {
                  const scopeLower = scope.toLowerCase().trim();

                  // Try exact match
                  let targetType = scopeToTypeMap.get(scopeLower);
                  if (targetType) return targetType;

                  // Try partial match
                  for (const [url, type] of scopeToTypeMap.entries()) {
                    if (scopeLower.includes(url) || url.includes(scopeLower)) {
                      return type;
                    }
                  }

                  // Try domain match
                  const scopeDomain = extractDomain(scopeLower);
                  for (const [url, type] of scopeToTypeMap.entries()) {
                    const targetDomain = extractDomain(url);
                    if (scopeDomain === targetDomain) {
                      return type;
                    }
                  }

                  return undefined;
                };

                // Calculate asset type distribution per severity level
                // Use reports_list if it has scope, otherwise use detailed_findings
                const severityAssetBreakdown: Record<string, Record<string, number>> = {
                  Critical: {},
                  High: {},
                  Medium: {},
                  Low: {},
                  Info: {}
                };

                // Check if reports_list has scope field
                const hasScope = reportData.reports_list?.some((r: any) => r.scope);
                const dataSource = hasScope ? reportData.reports_list : reportData.detailed_findings;

                (dataSource || []).forEach((item: any) => {
                  let sevRaw = item.severity_category || 'Info';
                  if (sevRaw.toLowerCase() === 'none') sevRaw = 'Info';
                  const severity = sevRaw.charAt(0).toUpperCase() + sevRaw.slice(1).toLowerCase();
                  const scope = item.scope;

                  if (severity && scope && severityAssetBreakdown[severity]) {
                    const targetType = findTargetType(scope);
                    if (targetType) {
                      severityAssetBreakdown[severity][targetType] = (severityAssetBreakdown[severity][targetType] || 0) + 1;
                    }
                  }
                });

                // Calculate total asset type counts (across all severities)
                // Use backend-calculated asset_type_counts if available, otherwise calculate from data
                let totalAssetCounts: Record<string, number> = {};

                if (reportData.asset_type_counts && reportData.asset_type_counts.length > 0) {
                  // Use backend-calculated counts (always fresh from database)
                  reportData.asset_type_counts?.forEach(item => {
                    totalAssetCounts[item.displayName] = item.count;
                  });
                } else {
                  // Fallback: Calculate from frontend data
                  // Initialize with all scoped types (including zero counts)
                  scopedTypes.forEach(type => {
                    totalAssetCounts[type] = 0;
                  });

                  // Add actual counts from severity breakdown
                  Object.values(severityAssetBreakdown).forEach(breakdown => {
                    Object.entries(breakdown).forEach(([type, count]) => {
                      totalAssetCounts[type] = (totalAssetCounts[type] || 0) + count;
                    });
                  });
                }

                // Format total asset distribution
                const formatTotalAssetDistribution = () => {
                  const entries = Object.entries(totalAssetCounts);
                  if (entries.length === 0 || !hasMultipleAssetTypes) return null;

                  return entries.map(([type, count], idx) => {
                    const isLast = idx === entries.length - 1;
                    const isSecondLast = idx === entries.length - 2;
                    return (
                      <Text key={idx}>
                        <Text style={{ fontWeight: 'bold' }}>{count}</Text> in <Text style={{ fontWeight: 'bold' }}>{type}</Text>
                        {isLast ? '' : isSecondLast ? ', and ' : ', '}
                      </Text>
                    );
                  });
                };

                return (
                  <Text style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 10, color: '#374151', textAlign: 'justify' }}>
                    {(reportData.branding_company || DEFAULT_COMPANY)}'s thorough assessment identified <Text style={{ fontWeight: 'bold' }}>{reportData.total_findings}</Text> {reportData.total_findings === 1 ? 'finding' : 'findings'}
                    {hasMultipleAssetTypes && (
                      <Text> across multiple asset types including {scopedTypes.map((type, idx) => {
                        const isLast = idx === scopedTypes.length - 1;
                        const isSecondLast = idx === scopedTypes.length - 2;
                        return (
                          <Text key={idx}>
                            <Text style={{ fontWeight: 'bold' }}>{type}</Text>
                            {isLast ? '' : isSecondLast ? ' and ' : ', '}
                          </Text>
                        );
                      })}</Text>
                    )}
                    , with <Text style={{ fontWeight: 'bold', color: '#8b0000' }}>{reportData.open_close_counts_by_severity?.Critical?.Total || 0}</Text> categorized as <Text style={{ fontWeight: 'bold', color: '#8b0000' }}>Critical Severity</Text>, <Text style={{ fontWeight: 'bold', color: '#ff4500' }}>{reportData.open_close_counts_by_severity?.High?.Total || 0}</Text> categorized as <Text style={{ fontWeight: 'bold', color: '#ff4500' }}>High Severity</Text>, <Text style={{ fontWeight: 'bold', color: '#ffd700' }}>{reportData.open_close_counts_by_severity?.Medium?.Total || 0}</Text> categorized as <Text style={{ fontWeight: 'bold', color: '#ffd700' }}>Medium Severity</Text>, <Text style={{ fontWeight: 'bold', color: '#32cd32' }}>{reportData.open_close_counts_by_severity?.Low?.Total || 0}</Text> as <Text style={{ fontWeight: 'bold', color: '#32cd32' }}>Low Severity</Text>, and <Text style={{ fontWeight: 'bold', color: '#64748b' }}>{reportData.open_close_counts_by_severity?.Info?.Total || 0}</Text> as <Text style={{ fontWeight: 'bold', color: '#64748b' }}>Info Severity</Text>.
                    {formatTotalAssetDistribution() && (
                      <Text> The vulnerability distribution across asset types shows {formatTotalAssetDistribution()}.</Text>
                    )}
                    {' '}During the assessment, all {generateSeverityText()} were reported to the {reportData.company_name} team, and the client addressed the reported vulnerabilities concurrently.
                  </Text>
                );
              })()}

              {/* Dynamic Status Summary Paragraph */}
              <Text style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 10, color: '#374151', textAlign: 'justify' }}>
                Of the <Text style={{ fontWeight: 'bold' }}>{reportData.total_findings}</Text> identified vulnerabilities, <Text style={{ fontWeight: 'bold', color: '#dc2626' }}>{reportData.total_open || 0}</Text> {reportData.total_open === 1 ? 'remains' : 'remain'} <Text style={{ fontWeight: 'bold', color: '#dc2626' }}>open</Text> and require immediate attention, while <Text style={{ fontWeight: 'bold', color: '#16a34a' }}>{reportData.total_closed || 0}</Text> {reportData.total_closed === 1 ? 'has' : 'have'} been successfully <Text style={{ fontWeight: 'bold', color: '#16a34a' }}>remediated and closed</Text>.
                {((reportData.total_business_accepts_risk || 0) > 0 || (reportData.total_business_non_actionable || 0) > 0) && (
                  <Text>
                    {' '}Additionally,
                    {(reportData.total_business_accepts_risk || 0) > 0 && (
                      <Text>
                        {' '}<Text style={{ fontWeight: 'bold', color: '#f59e0b' }}>{reportData.total_business_accepts_risk}</Text> {reportData.total_business_accepts_risk === 1 ? 'vulnerability has' : 'vulnerabilities have'} been marked as <Text style={{ fontWeight: 'bold', color: '#f59e0b' }}>business accepts risk</Text> after careful evaluation of the risk-to-impact ratio
                      </Text>
                    )}
                    {(reportData.total_business_accepts_risk || 0) > 0 && (reportData.total_business_non_actionable || 0) > 0 && (
                      <Text>, and </Text>
                    )}
                    {(reportData.total_business_non_actionable || 0) > 0 && (
                      <Text>
                        {(reportData.total_business_accepts_risk || 0) === 0 && ' '}<Text style={{ fontWeight: 'bold', color: '#6b7280' }}>{reportData.total_business_non_actionable}</Text> {reportData.total_business_non_actionable === 1 ? 'has' : 'have'} been classified as <Text style={{ fontWeight: 'bold', color: '#6b7280' }}>non-actionable</Text> due to environmental constraints or business requirements
                      </Text>
                    )}
                    <Text>.</Text>
                  </Text>
                )}
              </Text>

              <Text style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 18, color: '#374151', textAlign: 'justify' }}>
                The {(reportData.branding_company || DEFAULT_COMPANY)} team has documented the identified vulnerabilities along with their current statuses in the key findings section of this report. Prompt action is recommended to strengthen the application's overall security posture.
              </Text>
            </>
          )}
          <View style={{
            borderRadius: 6,
            borderWidth: 1,
            borderColor: '#cbd5e1',
            marginTop: 20,
            marginBottom: 16,
            width: '100%',
            alignSelf: 'center',
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}>
            {/* Dynamic Table Header - Matching Scope Table Style */}
            <View style={{
              flexDirection: 'row',
              backgroundColor: '#1e40af',
              borderBottomWidth: 2,
              borderBottomColor: '#1e3a8a',
            }}>
              <Text style={{
                paddingVertical: 8,
                paddingHorizontal: 10,
                textAlign: 'center',
                fontWeight: 'bold',
                width: columnWidths.severity,
                color: '#fff',
                fontSize: 10,
                lineHeight: 1.3,
              }}>Severity</Text>
              <Text style={{
                paddingVertical: 8,
                paddingHorizontal: 10,
                textAlign: 'center',
                fontWeight: 'bold',
                width: columnWidths.open,
                color: '#fff',
                fontSize: 10,
                lineHeight: 1.3,
              }}>Open</Text>
              <Text style={{
                paddingVertical: 8,
                paddingHorizontal: 10,
                textAlign: 'center',
                fontWeight: 'bold',
                width: columnWidths.closed,
                color: '#fff',
                fontSize: 10,
                lineHeight: 1.3,
              }}>Closed</Text>
              {hasAcceptsRisk && (
                <Text style={{
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  width: columnWidths.acceptsRisk,
                  color: '#fff',
                  fontSize: 9,
                  lineHeight: 1.3,
                }}>Accepts Risk</Text>
              )}
              {hasNonActionable && (
                <Text style={{
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  width: columnWidths.nonActionable,
                  color: '#fff',
                  fontSize: 9,
                  lineHeight: 1.3,
                }}>Non-Actionable</Text>
              )}
              <Text style={{
                paddingVertical: 8,
                paddingHorizontal: 10,
                textAlign: 'center',
                fontWeight: 'bold',
                width: columnWidths.total,
                color: '#fff',
                fontSize: 10,
                lineHeight: 1.3,
              }}>Total</Text>
            </View>
            {/* Dynamic Table Rows - Matching Scope Table Style */}
            {[
              {
                label: 'Critical',
                color: '#dc2626',
                icon: '●',
                data: reportData.open_close_counts_by_severity?.Critical || {},
              },
              {
                label: 'High',
                color: '#ea580c',
                icon: '●',
                data: reportData.open_close_counts_by_severity?.High || {},
              },
              {
                label: 'Medium',
                color: '#eab308',
                icon: '●',
                data: reportData.open_close_counts_by_severity?.Medium || {},
              },
              {
                label: 'Low',
                color: '#16a34a',
                icon: '●',
                data: reportData.open_close_counts_by_severity?.Low || {},
              },
              {
                label: 'Info',
                color: '#64748b',
                icon: '●',
                data: reportData.open_close_counts_by_severity?.Info || {},
              },
            ].map((row, idx, arr) => (
              <View key={row.label} style={{
                flexDirection: 'row',
                backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff',
                borderBottomWidth: idx === arr.length - 1 ? 0 : 1,
                borderBottomColor: '#e2e8f0',
                minHeight: 28,
                alignItems: 'center',
              }}>
                <View style={{
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  width: columnWidths.severity,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}>
                  <Text style={{
                    color: row.color,
                    fontSize: 11,
                    marginRight: 5,
                    fontWeight: 'bold',
                  }}>{row.icon}</Text>
                  <Text style={{
                    fontWeight: 'bold',
                    color: '#475569',
                    fontSize: 10,
                    lineHeight: 1.3,
                  }}>{row.label}</Text>
                </View>
                <Text style={{
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  width: columnWidths.open,
                  textAlign: 'center',
                  fontSize: 10,
                  color: '#475569',
                  lineHeight: 1.3,
                }}>{row.data.Open || 0}</Text>
                <Text style={{
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  width: columnWidths.closed,
                  textAlign: 'center',
                  fontSize: 10,
                  color: '#475569',
                  lineHeight: 1.3,
                }}>{row.data.Closed || 0}</Text>
                {hasAcceptsRisk && (
                  <Text style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    width: columnWidths.acceptsRisk,
                    textAlign: 'center',
                    fontSize: 10,
                    color: '#475569',
                    lineHeight: 1.3,
                  }}>{row.data.BusinessAcceptsRisk || 0}</Text>
                )}
                {hasNonActionable && (
                  <Text style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    width: columnWidths.nonActionable,
                    textAlign: 'center',
                    fontSize: 10,
                    color: '#475569',
                    lineHeight: 1.3,
                  }}>{row.data.BusinessNonActionable || 0}</Text>
                )}
                <Text style={{
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  width: columnWidths.total,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: 10,
                  color: '#1e293b',
                  lineHeight: 1.3,
                }}>{row.data.Total || 0}</Text>
              </View>
            ))}
            {/* Dynamic Total Row - Matching Scope Table Style */}
            <View style={{
              flexDirection: 'row',
              backgroundColor: '#dbeafe',
              borderTopWidth: 2,
              borderTopColor: '#93c5fd',
              minHeight: 28,
              alignItems: 'center',
            }}>
              <Text style={{
                paddingVertical: 6,
                paddingHorizontal: 10,
                width: columnWidths.severity,
                fontWeight: 'bold',
                color: '#1e40af',
                fontSize: 10,
                lineHeight: 1.3,
              }}>TOTAL</Text>
              <Text style={{
                paddingVertical: 6,
                paddingHorizontal: 10,
                width: columnWidths.open,
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#1e40af',
                fontSize: 10,
                lineHeight: 1.3,
              }}>{reportData.total_open || 0}</Text>
              <Text style={{
                paddingVertical: 6,
                paddingHorizontal: 10,
                width: columnWidths.closed,
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#1e40af',
                fontSize: 10,
                lineHeight: 1.3,
              }}>{reportData.total_closed || 0}</Text>
              {hasAcceptsRisk && (
                <Text style={{
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  width: columnWidths.acceptsRisk,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: '#1e40af',
                  fontSize: 10,
                  lineHeight: 1.3,
                }}>{reportData.total_business_accepts_risk || 0}</Text>
              )}
              {hasNonActionable && (
                <Text style={{
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  width: columnWidths.nonActionable,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: '#1e40af',
                  fontSize: 10,
                  lineHeight: 1.3,
                }}>{reportData.total_business_non_actionable || 0}</Text>
              )}
              <Text style={{
                paddingVertical: 6,
                paddingHorizontal: 10,
                width: columnWidths.total,
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#1e40af',
                fontSize: 10,
                lineHeight: 1.3,
              }}>{reportData.total_findings || 0}</Text>
            </View>
          </View>
          <Text style={{
            fontSize: 10,
            color: '#64748b',
            textAlign: 'center',
            marginTop: 4,
            fontStyle: 'italic',
            lineHeight: 1.3,
          }}>
            Table 1: Key Findings Summary
          </Text>
        </View>
      </View>
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>{reportData.document_number || 'Document Number'}</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
      <Text style={{ display: 'none' }} render={({ pageNumber }) => { updateSectionPage('KeyFindings', pageNumber); return ''; }} fixed />
    </Page >
  );
};

export default KeyFindingsPage; 